/**
 * Sets a cookie.
 * @param {string} name - Name for the cookie.
 * @param {string} value - Value for the cookie.
 * @param {number} days - Number of days until the cookie should expire.
 */
function setCookie(name, value, days) {
  let expires = '';

  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = `; expires=${date.toUTCString()}`;
  }

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(String(value))}${expires}; path=/; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
}

/**
 * Gets the value of a cookie (if it exists).
 * @param {string} name - Name of the cookie.
 * @returns {?string}
 */
function getCookie(name) {
  const encodedName = encodeURIComponent(name);
  const cookieString = `; ${document.cookie}`;

  for (const tryName of [name, encodedName]) {
    const cookies = cookieString.split(`; ${tryName}=`);
    if (cookies.length !== 2) continue;

    const raw = cookies.pop().split(';').shift();
    if (!raw) continue;

    try {
      return decodeURIComponent(raw);
    } catch (e) {
      return raw;
    }
  }

  return null;
}

function deleteCookie(name) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax${secure}`;
}

function isLoggedInCustomer() {
  return document.body?.getAttribute('data-customer-logged-in') === 'true';
}

const NEWSLETTER_SUBSCRIBED_COOKIE = 'aesthet_newsletter_subscribed';

function isLoggedInWelcomeSubscriber() {
  if (!isLoggedInCustomer()) return false;
  return document.body?.getAttribute('data-customer-welcome-subscribed') === 'true';
}

function isGuestNewsletterSubscribed(popupId) {
  if (isLoggedInCustomer()) return false;
  return Boolean(getCookie(NEWSLETTER_SUBSCRIBED_COOKIE) || getCookie(`${popupId}-subscribed`));
}

function shouldHideWelcomeOffer(popupId) {
  return isLoggedInWelcomeSubscriber() || isGuestNewsletterSubscribed(popupId);
}

function clearStaleGuestWelcomePopupCookies() {
  if (!isLoggedInCustomer() || isLoggedInWelcomeSubscriber()) return;

  document.querySelectorAll('pop-up').forEach((popup) => {
    deleteCookie(`${popup.id}-subscribed`);
    deleteCookie(`${popup.id}-dismissed`);
  });
}

function canAutoOpenWelcomePopup(popup) {
  if (popup.dataset.showOnMobile === 'true') return true;
  return typeof theme !== 'undefined' && theme.mediaMatches && theme.mediaMatches.md;
}

/* global Modal */

const WELCOME_POPUP_WAS_SUBMITTED_KEY = 'aesthet_welcome_popup_wasSubmitted';
const WELCOME_POPUP_HEIGHT_KEY = 'aesthet_welcome_popup_height';

function isShopifyCustomerPosted() {
  try {
    return new URLSearchParams(window.location.search).get('customer_posted') === 'true';
  } catch (e) {
    return window.location.search.includes('customer_posted=true');
  }
}

function wasWelcomePopupFormSubmitted() {
  try {
    return sessionStorage.getItem(WELCOME_POPUP_WAS_SUBMITTED_KEY) === '1';
  } catch (e) {
    return false;
  }
}

function stripCustomerPostedFromUrl() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('customer_posted')) return;

    url.searchParams.delete('customer_posted');
    const search = url.searchParams.toString();
    const cleanUrl = url.pathname + (search ? `?${search}` : '') + url.hash;
    history.replaceState(null, '', cleanUrl);
  } catch (e) {
    // ignore
  }
}

/**
 * writing-mode: vertical-rl often works on a div in WebKit but is ignored on
 * <button> (UA appearance). Probe the real teaser, not a generic div.
 * @param {HTMLElement} teaser
 * @returns {boolean}
 */
function isPopupTeaserVerticalWritingModeBroken(teaser) {
  if (!teaser || typeof document === 'undefined' || !document.body) return false;

  const probe = teaser.cloneNode(true);
  probe.removeAttribute('id');
  probe.classList.remove('is-visible', 'popup-teaser--vertical-fallback');

  const wrappedInner = probe.querySelector('.popup-teaser__inner');
  if (wrappedInner) {
    while (wrappedInner.firstChild) {
      probe.appendChild(wrappedInner.firstChild);
    }
    wrappedInner.remove();
  }

  probe.style.setProperty('display', 'inline-flex', 'important');
  probe.style.setProperty('visibility', 'hidden', 'important');
  probe.style.setProperty('opacity', '0', 'important');
  probe.style.setProperty('pointer-events', 'none', 'important');
  probe.style.setProperty('position', 'absolute', 'important');
  probe.style.setProperty('left', '0', 'important');
  probe.style.setProperty('top', '0', 'important');
  probe.style.setProperty('transform', 'none', 'important');
  probe.style.setProperty('appearance', 'none', 'important');
  probe.style.setProperty('-webkit-appearance', 'none', 'important');
  probe.style.setProperty('writing-mode', 'vertical-rl', 'important');
  probe.style.setProperty('-webkit-writing-mode', 'vertical-rl', 'important');
  document.body.appendChild(probe);

  const broken = probe.offsetWidth > 0 && probe.offsetHeight <= probe.offsetWidth;
  probe.remove();
  return broken;
}

/**
 * Wrap teaser content so the fallback CSS can rotate the inner panel
 * around a 0×0 point at left:0 / top:50%.
 * @param {HTMLElement|null|undefined} teaser
 */
function applyPopupTeaserVerticalFallback(teaser) {
  if (!teaser || teaser.classList.contains('popup-teaser--vertical-fallback')) return;
  if (!isPopupTeaserVerticalWritingModeBroken(teaser)) return;

  const label = teaser.querySelector('.popup-teaser__label');
  const close = teaser.querySelector('.popup-teaser__close');
  if (!label || !close) return;

  const inner = document.createElement('span');
  inner.className = 'popup-teaser__inner';
  inner.appendChild(close);
  inner.appendChild(label);
  teaser.appendChild(inner);
  teaser.classList.add('popup-teaser--vertical-fallback');
}

function initPopupTeaserVerticalFallbacks() {
  document.querySelectorAll('.popup-teaser').forEach(applyPopupTeaserVerticalFallback);
}

function definePopUpElement() {
  if (customElements.get('pop-up')) return;

  class PopUp extends Modal {
    constructor() {
      super();
      this.cookie = `${this.id}-dismissed`;
      this.subscribedCookie = `${this.id}-subscribed`;
      this.wasOpened = false;
      this.teaser = document.getElementById(`${this.id}-teaser`);
      this.initTeaser();
      this.initPopupNewsletterTracking();
      this.applySubscriptionSuccessLayout();

      if (this.handlePostSubscribeReload()) {
        return;
      }

      if (shouldHideWelcomeOffer(this.id)) {
        this.hideTeaserPermanently();
        return;
      }

      if (Shopify.designMode) {
        document.addEventListener('shopify:section:select', (evt) => {
          if (evt.target === this.closest('.shopify-section')) this.open();
        });

        document.addEventListener('shopify:section:deselect', this.close.bind(this));
      } else if (!getCookie(this.cookie) && canAutoOpenWelcomePopup(this)) {
        if (this.dataset.trigger === 'delay') {
          setTimeout(() => this.open(), Number(this.dataset.delay) * 1000);
        } else if (this.dataset.trigger === 'exit' && typeof theme !== 'undefined' && theme.mediaMatches?.md) {
          this.mouseLeaveHandler = this.mouseLeaveHandler || this.handleMouseLeave.bind(this);
          document.body.addEventListener('mouseleave', this.mouseLeaveHandler);
        }
      } else if (getCookie(this.cookie) && !shouldHideWelcomeOffer(this.id)) {
        this.showTeaser();
      }
    }

    /**
     * After pop-up subscribe, Shopify reloads with customer_posted=true.
     * Show success once (same navigation as submit), then strip the query param
     * so refresh does not reopen the modal or re-render the success state.
     * @returns {boolean}
     */
    handlePostSubscribeReload() {
      if (!isShopifyCustomerPosted()) {
        return false;
      }

      const hasPopupSuccess = Boolean(this.querySelector('[data-popup-newsletter-form] .signup-form__response--success'));
      if (!hasPopupSuccess) {
        return false;
      }

      if (!isLoggedInCustomer()) {
        setCookie(this.subscribedCookie, true, 3650);
        setCookie('aesthet_newsletter_subscribed', '1', 3650);
      }

      this.hideTeaserPermanently();

      const shouldShowSuccessModal = wasWelcomePopupFormSubmitted();

      try {
        sessionStorage.removeItem(WELCOME_POPUP_WAS_SUBMITTED_KEY);
      } catch (e) {
        // ignore
      }

      stripCustomerPostedFromUrl();

      if (shouldShowSuccessModal) {
        this.open();
      }

      return true;
    }

    applySubscriptionSuccessLayout() {
      const successResponse = this.querySelector(
        '[data-popup-newsletter-form] .signup-form__response--success'
      );
      if (!successResponse) return;

      const modalWindow = this.querySelector('.modal__window');
      const content = this.querySelector('.modal__content');

      if (modalWindow) {
        modalWindow.classList.add('is-subscription-success');
      }

      if (content) {
        content.classList.add('is-subscription-success');
      }

      let storedHeight = null;
      try {
        storedHeight = sessionStorage.getItem(WELCOME_POPUP_HEIGHT_KEY);
        sessionStorage.removeItem(WELCOME_POPUP_HEIGHT_KEY);
      } catch (e) {
        // ignore
      }

      if (storedHeight && modalWindow) {
        modalWindow.style.minHeight = `${storedHeight}px`;
        modalWindow.dataset.subscriptionSuccessHeight = 'stored';
      }
    }

    initPopupNewsletterTracking() {
      const form = this.querySelector('[data-popup-newsletter-form] form');
      if (!form || form.dataset.popupTrackBound === 'true') return;

      form.dataset.popupTrackBound = 'true';
      form.addEventListener('submit', () => {
        const modalWindow = this.querySelector('.modal__window');

        try {
          sessionStorage.setItem(WELCOME_POPUP_WAS_SUBMITTED_KEY, '1');

          if (modalWindow) {
            sessionStorage.setItem(WELCOME_POPUP_HEIGHT_KEY, String(modalWindow.offsetHeight));
          }
        } catch (e) {
          // ignore
        }
      });
    }

    disconnectedCallback() {
      if (this.mouseLeaveHandler) {
        document.body.removeEventListener('mouseleave', this.mouseLeaveHandler);
      }
    }

    handleMouseLeave(evt) {
      if (evt.clientY < 0 && !getCookie(this.cookie)) this.open();
    }

    open(opener) {
      this.wasOpened = true;
      this.hideTeaser();
      super.open(opener);
    }

    close() {
      super.close();
      setCookie(this.cookie, true, parseInt(this.dataset.dismissDays, 10));

      if (this.wasOpened) {
        this.wasOpened = false;
        if (!shouldHideWelcomeOffer(this.id)) {
          this.showTeaser();
        }
      }
    }

    initTeaser() {
      if (!this.teaser) return;

      applyPopupTeaserVerticalFallback(this.teaser);

      this.teaser.addEventListener('click', (evt) => {
        if (evt.target.closest('.popup-teaser__close')) {
          evt.preventDefault();
          evt.stopPropagation();
          this.hideTeaser();
        } else {
          this.hideTeaser();
          this.open(this.teaser);
        }
      });
    }

    showTeaser() {
      if (!this.teaser) return;
      this.teaser.style.display = '';
      this.teaser.classList.add('is-visible');
    }

    hideTeaser() {
      if (!this.teaser) return;
      this.teaser.classList.remove('is-visible');
    }

    hideTeaserPermanently() {
      if (!this.teaser) return;
      this.teaser.style.display = 'none';
      this.teaser.classList.remove('is-visible');
    }
  }

  customElements.define('pop-up', PopUp);
}

if (customElements.get('modal-dialog')) {
  definePopUpElement();
} else {
  customElements.whenDefined('modal-dialog').then(definePopUpElement);
}

function hideSubscribedWelcomePopupTeasers() {
  document.querySelectorAll('pop-up').forEach((popup) => {
    if (!shouldHideWelcomeOffer(popup.id)) return;

    const teaser = document.getElementById(`${popup.id}-teaser`);
    if (teaser) {
      teaser.style.display = 'none';
      teaser.classList.remove('is-visible');
    }
  });
}

clearStaleGuestWelcomePopupCookies();
document.addEventListener('DOMContentLoaded', () => {
  initPopupTeaserVerticalFallbacks();
  hideSubscribedWelcomePopupTeasers();
});
